import { useIntl } from "react-intl";

import FeatureGates from "@atlassian/feature-gate-js-client";

import {
    ToastComponent,
    ToastComponentProps
} from "@/components/wpl/toast/src";
import { useLocalizedHref } from "@/libraries/web-framework";

export const ToastSection = () => {
    const intl = useIntl();
    const atlassianRecommendPropsForJsw: Record<
        "high-trust" | "cloud-value" | "high-complexity" | "blocked" | "access",
        ToastComponentProps
    > = {
        "high-trust": {
            title: intl.formatMessage({
                id: "JiraSoftwarePage.toast.title.high.trust",
                defaultMessage: "Got Trust issues?"
            }),
            description: intl.formatMessage({
                id: "JiraSoftwarePage.toast.description.high.trust",
                defaultMessage:
                    "See how we meet your data protection needs on Cloud."
            }),
            cta: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePage.toast.cta.learn.more",
                    defaultMessage: "Learn more"
                }),
                url: useLocalizedHref("/migration/plan/security-compliance")
            }
        },
        "cloud-value": {
            title: intl.formatMessage({
                id: "JiraSoftwarePage.toast.title.cloud.value",
                defaultMessage: "Work is changing. Are you keeping up?"
            }),
            description: intl.formatMessage({
                id: "JiraSoftwarePage.toast.description.cloud.value",
                defaultMessage:
                    "See how your teams can work better together on Cloud."
            }),

            cta: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePage.toast.cta.why.migrate",
                    defaultMessage: "Why migrate?"
                }),
                url: useLocalizedHref("/migration/assess/why-cloud")
            }
        },
        "high-complexity": {
            title: intl.formatMessage({
                id: "JiraSoftwarePage.toast.title.high.complexity",
                defaultMessage: "Does a move to Cloud feel too complex?"
            }),
            description: intl.formatMessage({
                id: "JiraSoftwarePage.toast.description.high.complexity",
                defaultMessage:
                    "Get a recommendation from a Cloud Specialized Solution Partner."
            }),
            cta: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePage.toast.cta.learn.more",
                    defaultMessage: "Learn more"
                }),
                url: useLocalizedHref("/migration/help/find-migration-partner")
            }
        },
        "blocked": {
            title: intl.formatMessage({
                id: "JiraSoftwarePage.toast.title.blocked",
                defaultMessage: "Evaluating if Cloud meets your needs?"
            }),
            description: intl.formatMessage({
                id: "JiraSoftwarePage.toast.description.blocked",
                defaultMessage: "Stay up-to-date on our"
            }),
            cta: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePage.toast.cta.cloud.roadmap",
                    defaultMessage: "Cloud Roadmap"
                }),
                url: useLocalizedHref("/roadmap/cloud")
            }
        },
        "access": {
            title: intl.formatMessage({
                id: "JiraSoftwarePage.toast.title.access",
                defaultMessage: "Atlassian Access: centralized IAM security"
            }),
            description: intl.formatMessage({
                id: "JiraSoftwarePage.toast.description.access",
                defaultMessage:
                    "Centralize single sign-on (SSO) policies across Atlassian cloud."
            }),

            cta: {
                text: intl.formatMessage({
                    id: "JiraSoftwarePage.toast.cta.discover.access",
                    defaultMessage: "Discover Access"
                }),
                url: useLocalizedHref("/software/access")
            }
        }
    };

    const evaluateFeatureFlag: Record<
        "high-trust" | "cloud-value" | "high-complexity" | "blocked" | "access",
        boolean
    > = {
        "high-trust": FeatureGates.checkGate(
            "gwp-atlassian-recommends-mig-high-trust-gate"
        ),
        "cloud-value": FeatureGates.checkGate(
            "gwp-atlassian-recommends-mig-cloud-value-gate"
        ),
        "high-complexity": FeatureGates.checkGate(
            "gwp-atlassian-recommends-mig-high-complexity-gate"
        ),
        "blocked": FeatureGates.checkGate(
            "gwp-atlassian-recommends-mig-blocked-gate"
        ),
        "access": FeatureGates.checkGate(
            "gwp-atlassian-recommends-jsw-access-gate"
        )
    };

    const toastCohort = (
        Object.keys(evaluateFeatureFlag) as Array<
            keyof typeof evaluateFeatureFlag
        >
    ).find((item) => evaluateFeatureFlag[item]);

    return (
        toastCohort && (
            <ToastComponent
                {...atlassianRecommendPropsForJsw[toastCohort]}
                cohort={toastCohort}
                product="jiraSoftware"
            />
        )
    );
};
